import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { PrinterProfile, PrinterProfileType, PrinterProfileConfig } from '../types/printerProfile';

interface PrinterProfileDB extends DBSchema {
  printer_profiles: {
    key: string;
    value: PrinterProfile;
    indexes: { 'by-active': boolean };
  };
}

const DB_NAME = 'printer-profile-db';
const STORE_NAME = 'printer_profiles';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<PrinterProfileDB> | null = null;

const initDB = async (): Promise<IDBPDatabase<PrinterProfileDB>> => {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<PrinterProfileDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('by-active', 'is_active');
      }
    },
  });

  const profiles = await dbInstance.getAll(STORE_NAME);
  if (profiles.length === 0) {
    await initializeDefaultProfiles(dbInstance);
  }

  // ⚠️ APPEL INCONDITIONNEL, et c'est le point délicat du portage : `initializeDefaultProfiles`
  // ne s'exécute QUE si la base est vide. Sur une borne DÉJÀ INSTALLÉE, elle ne l'est
  // jamais — sans cette ligne, les modules portés n'apparaîtraient jamais dans l'écran
  // « Profils d'impression » et le travail serait invisible pour l'exploitant.
  await ensurePortedProfiles(dbInstance);

  return dbInstance;
};

const initializeDefaultProfiles = async (db: IDBPDatabase<PrinterProfileDB>) => {
  const legacyProfile: PrinterProfile = {
    id: 'legacy-default',
    name: 'Ancien Module (Legacy)',
    type: 'legacy',
    config: {
      width: 58,
      useEscPos: true,
      commands: {
        large: '\\x1B\\x21\\x18',
        medium: '\\x1B\\x21\\x08',
        normal: '\\x1B\\x21\\x00',
      },
      alignmentTags: {
        center: '[C]',
        left: '[L]',
        right: '[R]',
      },
      boldTags: '**text**',
      paddingWidth: 20,
      separator: '----------------',
      showCategories: true,
      showTaxDetails: true,
      aggregateOptions: true,
      formatNotes: false,
    },
    is_active: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const modernProfile: PrinterProfile = {
    id: 'modern-default',
    name: 'Nouveau Module (Modern)',
    type: 'modern',
    config: {
      width: 42,
      useEscPos: true,
      showCategories: true,
      showTaxDetails: true,
      aggregateOptions: true,
      formatNotes: true,
      paddingWidth: 20,
      separator: '------------------------------------------',
    },
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  await db.put(STORE_NAME, legacyProfile);
  await db.put(STORE_NAME, modernProfile);
};

/* ═══════════════════════════════════════════════════════════════════════════════
 * PROFILS DES MODULES PORTÉS DEPUIS LA CONSOLE
 * ═══════════════════════════════════════════════════════════════════════════════
 * Sept modules, sept profils, TOUS INACTIFS à l'installation : le comportement par
 * défaut de la borne (profil « Nouveau Module (Modern) ») ne change pas d'un octet.
 * L'exploitant en active un depuis « Périphériques » → « Profils d'impression », et
 * revient en arrière de la même façon. Aucune bascule n'est exposée au client final.
 *
 * ⚠️ `useEscPos: false` sur TOUS : ce drapeau fait injecter des OCTETS ESC/POS BRUTS
 * par les chemins historiques. Devant une ligne `<img>`, un octet brut fait échouer la
 * détection de colonne unique de DantSu et l'hexadécimal s'imprime EN CLAIR ; sur Star
 * il est DESSINÉ comme un glyphe ; sur Sunmi il part tel quel.
 *
 * ⚠️ Séparateurs en `=`, JAMAIS 24 tirets consécutifs : dès que le tampon du
 * pré-traitement natif contient `------------------------`, deux lignes identiques
 * consécutives font DISPARAÎTRE la seconde (un article commandé 2× de suite s'évapore)
 * et un séparateur parasite est ajouté en fin de ticket.
 *
 * ⚠️ Largeurs : la borne compose aujourd'hui en 42 colonnes, ce qui n'est NI 32
 * (58 mm) NI 48 (80 mm) — les deux seules valeurs que l'app Android sait produire
 * (`paperWidth == 48f ? 32 : 48`). Les profils portés proposent donc les deux vraies
 * valeurs. En cas de doute : 32 s'imprime correctement sur une 80 mm (le pilote comble
 * à droite), l'inverse est faux — une ligne de 48 sur une 58 mm est tronquée.
 */

const SEP_48 = '================================================';
const SEP_32 = '================================';

const portedProfile = (
  id: string,
  name: string,
  type: PrinterProfileType,
  config: PrinterProfileConfig
): PrinterProfile => ({
  id,
  name,
  type,
  config: {
    paddingWidth: 20,
    useEscPos: false,
    showCategories: true,
    showTaxDetails: true,
    aggregateOptions: true,
    formatNotes: true,
    taxDisplay: { displayMode: 'detailed' },
    ...config,
  },
  is_active: false,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
});

const getPortedProfiles = (): PrinterProfile[] => [
  portedProfile('ported-unified-58', 'Module Unifié 58 mm (32 col.)', 'unified', {
    width: 32,
    separator: SEP_32,
  }),
  portedProfile('ported-unified-80', 'Module Unifié 80 mm (48 col.)', 'unified', {
    width: 48,
    separator: SEP_48,
  }),
  portedProfile('ported-unified-sunmi', 'Module Unifié Sunmi (texte brut, 32 col.)', 'unified', {
    width: 32,
    separator: SEP_32,
    // Sunmi imprime les balises LITTÉRALEMENT : on n'en émet aucune.
    plainText: true,
  }),
  portedProfile('ported-epson', 'Epson TM (ESC/POS, colonnes natives)', 'epson', {
    width: 48,
    separator: SEP_48,
  }),
  portedProfile('ported-star', 'Star TSP (aucun octet brut)', 'star', {
    width: 48,
    separator: SEP_48,
  }),
  portedProfile('ported-noanchor', 'Colonnes natives sans le mot ARTICLES', 'noanchor', {
    width: 48,
    separator: SEP_48,
  }),
  portedProfile('ported-antipattern', 'Anti-motif : quantité écrite « 2x Nom »', 'antipattern', {
    width: 48,
    separator: SEP_48,
  }),
  // Repris de la console (« USB Epson - Texte + bandeaux noirs ») : le look du ticket image
  // (infos clés en blanc sur noir) mais en TEXTE ESC/POS → aucune image, donc aucune
  // saturation du tampon sur les Epson en USB. Epson/génériques uniquement (pas Sunmi/Star).
  portedProfile('ported-usb-epson-banners', 'USB Epson - Texte + bandeaux noirs (80 mm)', 'noanchor', {
    width: 48,
    separator: SEP_48,
    bannerEmphasis: true,
  }),
  portedProfile('ported-raster', 'Image complète 80 mm (contourne la perte des prix)', 'raster', {
    width: 48,
    separator: SEP_48,
  }),
  portedProfile('ported-raster-hybrid', 'Image des articles seuls 80 mm', 'raster-hybrid', {
    width: 48,
    separator: SEP_48,
  }),
];

/** Identifiants des profils portés — protégés en suppression. */
const PORTED_PROFILE_IDS = new Set(getPortedProfiles().map((profile) => profile.id));

/**
 * Ajoute les profils portés MANQUANTS, sans jamais toucher à l'existant.
 *
 * ⚠️ On ne réécrit PAS un profil déjà présent : l'exploitant a pu en modifier la
 * largeur ou les séparateurs, et surtout `is_active` — le réécrire remettrait la borne
 * sur un autre module au prochain démarrage, en pleine journée de service.
 */
const ensurePortedProfiles = async (db: IDBPDatabase<PrinterProfileDB>): Promise<void> => {
  try {
    for (const profile of getPortedProfiles()) {
      const existing = await db.get(STORE_NAME, profile.id);
      if (!existing) {
        await db.put(STORE_NAME, profile);
      }
    }
  } catch (error) {
    // Un échec ici ne doit JAMAIS empêcher la borne d'imprimer avec ses profils actuels.
    console.error('[printerProfileService] Ajout des profils portés impossible:', error);
  }
};

export const getPrinterProfiles = async (): Promise<PrinterProfile[]> => {
  const db = await initDB();
  return await db.getAll(STORE_NAME);
};

export const resetToDefaultProfiles = async (): Promise<void> => {
  const db = await initDB();
  const profiles = await db.getAll(STORE_NAME);

  for (const profile of profiles) {
    if (profile.type === 'custom') {
      continue;
    }
    await db.delete(STORE_NAME, profile.id);
  }

  await initializeDefaultProfiles(db);
  // La réinitialisation a supprimé les profils portés (ils ne sont pas `custom`) :
  // on les recrée, inactifs, pour qu'ils restent proposés à l'exploitant.
  await ensurePortedProfiles(db);
};

export const getActiveProfile = async (): Promise<PrinterProfile | null> => {
  const db = await initDB();
  const profiles = await db.getAll(STORE_NAME);
  return profiles.find(p => p.is_active) || null;
};

export const setActiveProfile = async (id: string): Promise<void> => {
  const db = await initDB();
  const profiles = await db.getAll(STORE_NAME);

  for (const profile of profiles) {
    profile.is_active = profile.id === id;
    profile.updated_at = new Date().toISOString();
    await db.put(STORE_NAME, profile);
  }
};

export const createCustomProfile = async (
  name: string,
  config: PrinterProfileConfig
): Promise<PrinterProfile> => {
  const db = await initDB();

  const profile: PrinterProfile = {
    id: `custom-${Date.now()}`,
    name,
    type: 'custom',
    config,
    is_active: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  await db.put(STORE_NAME, profile);
  return profile;
};

export const updateProfile = async (
  id: string,
  updates: Partial<PrinterProfile>
): Promise<void> => {
  const db = await initDB();
  const profile = await db.get(STORE_NAME, id);

  if (!profile) {
    throw new Error(`Profile ${id} not found`);
  }

  const updatedProfile = {
    ...profile,
    ...updates,
    config: {
      ...profile.config,
      ...(updates.config || {}),
    },
    id: profile.id,
    updated_at: new Date().toISOString(),
  };

  await db.put(STORE_NAME, updatedProfile);
};

export const deleteProfile = async (id: string): Promise<void> => {
  const db = await initDB();
  const profile = await db.get(STORE_NAME, id);

  if (profile?.is_active) {
    throw new Error('Cannot delete active profile');
  }

  if (profile && (profile.type === 'legacy' || profile.type === 'modern')) {
    throw new Error('Cannot delete default profiles');
  }

  // Les profils portés sont des profils SYSTÈME : ils seraient de toute façon recréés
  // au prochain démarrage (`ensurePortedProfiles`). On refuse explicitement plutôt que
  // de laisser croire à une suppression.
  if (PORTED_PROFILE_IDS.has(id)) {
    throw new Error('Cannot delete a ported system profile');
  }

  await db.delete(STORE_NAME, id);
};

export const duplicateProfile = async (id: string, newName: string): Promise<PrinterProfile> => {
  const db = await initDB();
  const profile = await db.get(STORE_NAME, id);

  if (!profile) {
    throw new Error(`Profile ${id} not found`);
  }

  const duplicated: PrinterProfile = {
    ...profile,
    id: `custom-${Date.now()}`,
    name: newName,
    type: 'custom',
    is_active: false,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  await db.put(STORE_NAME, duplicated);
  return duplicated;
};

export const getProfileById = async (id: string): Promise<PrinterProfile | null> => {
  const db = await initDB();
  const profile = await db.get(STORE_NAME, id);
  return profile || null;
};
